import React, { useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { FILE_SIZE_LIMITS } from '../constants';

interface DropzoneAreaProps {
  acceptedFileTypes: Record<string, readonly string[]>;
  disabled: boolean;
  canAddMoreFiles: boolean;
  uploadType: 'images' | 'documents';
  mediaFilesLength: number;
  existingFilesLength: number;
  maxFiles: number;
  loading: boolean;
  uploadProgress: number;
  onDrop: (files: File[]) => void;
}

const DropzoneArea: React.FC<DropzoneAreaProps> = ({
  acceptedFileTypes,
  disabled,
  canAddMoreFiles,
  uploadType,
  mediaFilesLength,
  existingFilesLength,
  maxFiles,
  loading,
  uploadProgress,
  onDrop,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    disabled: disabled || !canAddMoreFiles,
    multiple: maxFiles > 1,
  });

  const fileCount = mediaFilesLength + existingFilesLength;
  const fileLabel = maxFiles === 1 ? 'archivo' : 'archivos';

  return (
    <Paper
      {...getRootProps()}
      elevation={3}
      sx={{
        p: 2,
        border: isDragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
        borderRadius: 2,
        textAlign: 'center',
        mb: 2,
        position: 'relative',
        backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
        cursor: disabled || !canAddMoreFiles ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <input {...getInputProps()} ref={inputRef} style={{ display: 'none' }} />

      <Typography variant="body2" color="text.secondary" component="span">
        {isDragActive
          ? maxFiles === 1
            ? 'Suelta el archivo aquí...'
            : 'Suelta los archivos aquí...'
          : maxFiles === 1
            ? 'Arrastra un archivo o haz clic'
            : 'Arrastra archivos o haz clic'}{' '}
        <Button
          variant="text"
          size="small"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || !canAddMoreFiles}
          sx={{
            p: 0,
            minWidth: 'unset',
            textTransform: 'none',
            fontWeight: 500,
            lineHeight: 1,
            ml: 0.5,
            mr: 0.5,
          }}
        >
          aquí
        </Button>
        para subir archivos
      </Typography>

      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        {uploadType === 'images'
          ? `Tamaño máximo: ${FILE_SIZE_LIMITS.IMAGE.MB}MB (fotos) / ${FILE_SIZE_LIMITS.VIDEO.MB}MB (videos)`
          : `Tamaño máximo: ${FILE_SIZE_LIMITS.DOCUMENT.MB}MB`}
      </Typography>

      <Typography variant="caption" color="text.secondary" display="block">
        {fileCount}/{maxFiles} {fileLabel}
      </Typography>

      {loading && (
        <Box mt={2}>
          <CircularProgress size={24} />
          <Typography variant="caption" display="block" mt={1}>
            {uploadProgress > 0
              ? `Subiendo... ${Math.round(uploadProgress)}%`
              : 'Procesando archivos...'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DropzoneArea;
